import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
interface AdminSession {
  email: string;
  startedAt: string;
}
interface AuthContextValue {
  session: AdminSession | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}
const SESSION_KEY = 'nicole.session.v1';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const AuthContext = createContext<AuthContextValue | null>(null);
function readSession(): AdminSession | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) as AdminSession : null;
  } catch {
    return null;
  }
}

/**
 * DEMO authentication adapter.
 * Replace `login` with a real provider (Supabase Auth, Clerk, Auth0…). Every write
 * operation must also be verified on the server — hiding UI is not protection.
 */
export function AuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [session, setSession] = useState<AdminSession | null>(readSession);
  const login = useCallback(async (email: string, password: string) => {
    await new Promise(resolve => setTimeout(resolve, 650));
    if (!EMAIL_PATTERN.test(email.trim())) throw new Error('Introduce un correo electrónico válido.');
    if (password.length < 8) throw new Error('Correo o contraseña incorrectos.');
    const next: AdminSession = {
      email: email.trim(),
      startedAt: new Date().toISOString()
    };
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(next));
    } catch {
      /* storage unavailable: keep in-memory session */
    }
    setSession(next);
  }, []);
  const logout = useCallback(() => {
    try {
      sessionStorage.removeItem(SESSION_KEY);
    } catch {
      /* ignore */
    }
    setSession(null);
  }, []);
  const value = useMemo(() => ({
    session,
    login,
    logout
  }), [session, login, logout]);
  return <AuthContext.Provider value={value} data-id="element-290" data-mp="5oq0ttbjulnr">{children}</AuthContext.Provider>;
}
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}